import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { showErrorToast } from "../../components/CustomComponents/toast/toast";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "../order/ManageOrderFilters.css";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";

const PurchaseSupplierReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  const [search, setSearch] = useState("");
  const pageKey = "purchaseSupplierReport";
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const searchTimeoutRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const defaultVisibleColumns = {
    itemName: true,
    purchaseDate: true,
    supplierName: true,
    receivedOrderedQty: true,
    unitPrice: true,
    totalCost: true,
    paymentStatus: true,
    invoiceNumber: true,
    poId: true,
    goToPO: true,
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "PurchaseDate", label: "Purchase Date" };

  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultLocation = { value: "all", label: "All Locations" };
  const defaultSupplier = { value: "all", label: "All Suppliers" };

  const defaultDateRange = [
    moment().startOf("month").toDate(),
    moment().endOf("month").toDate(),
  ];
  const defaultItemFilter = [];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const location = preferences.location || defaultLocation;
  const supplier = preferences.supplier || defaultSupplier;
  // Coerce cleared/null dates back to page defaults
  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const startOk = stored[0] != null && moment(stored[0]).isValid();
    const endOk = stored[1] != null && moment(stored[1]).isValid();
    if (!startOk && !endOk) return defaultDateRange;
    return [
      startOk ? stored[0] : defaultDateRange[0],
      endOk ? stored[1] : defaultDateRange[1],
    ];
  })();
  const itemFilter = Array.isArray(preferences.itemFilter) ? preferences.itemFilter : defaultItemFilter;
  const [rawStartDate, rawEndDate] = dateRange;

  const [showItemFilterModal, setShowItemFilterModal] = useState(false);
  const [filterProducts, setFilterProducts] = useState([]);
  const [loadingFilterProducts, setLoadingFilterProducts] = useState(false);

  const dispatch = useDispatch();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code;

  const sortByOptions = [
    { value: "PurchaseDate", label: "Purchase Date" },
    { value: "PO_ID", label: "PO ID" },
    { value: "SupplierName", label: "Supplier Name" },
    { value: "ItemName", label: "Menu Item Name" },
    { value: "QuantityOrdered", label: "Received / Ordered Qty" },
    { value: "QuantityReceived", label: "Quantity Received" },
    { value: "UnitPrice", label: "Unit Price" },
    { value: "TotalCost", label: "Total Cost" },
    { value: "PaymentStatus", label: "Payment Status" },
    { value: "InvoiceNumber", label: "PO Name" },
  ];

  const allLocationsOption = { value: "all", label: "All Locations" };
  const locationOptions = locations.length > 0
    ? [allLocationsOption, ...locations]
    : isMain && parentIdForLocation != null && !isNaN(parentIdForLocation)
      ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
      : [allLocationsOption];

  const supplierOptions = suppliers.length > 0 ? suppliers : [{ value: "all", label: "All Suppliers" }];

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey, field: filterName, value: selectedOption }));
    setPageNumber(1);
  };

  const resetFilters = () => {
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    dispatch(setFilterField({ pageKey, field: "supplier", value: defaultSupplier }));
    dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    setSearch("");
  };

  const clearAllFilters = () => {
    resetFilters();
    setPageNumber(1);
  };

  const clearItemFilter = () => {
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    setPageNumber(1);
  };

  const handleSearch = () => {
    setPageNumber(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    fetchReportData();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchReportData(), 500);
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    const valid = (d) => d && moment(d).isValid();
    // Picker clear → restore page defaults (same as Clear filters)
    if (!valid(newStartDate) && !valid(newEndDate)) {
      dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
      setPageNumber(1);
      return;
    }
    if ((newStartDate != null || newEndDate != null) && !valid(newStartDate) && !valid(newEndDate)) {
      showErrorToast("Invalid date selected");
      return;
    }
    let effectiveStartDate = null;
    let effectiveEndDate = null;
    if (valid(newStartDate) && !valid(newEndDate)) {
      effectiveStartDate = moment(newStartDate).startOf("day").toDate();
      effectiveEndDate = moment(newStartDate).endOf("day").toDate();
    } else if (!valid(newStartDate) && valid(newEndDate)) {
      effectiveStartDate = moment(newEndDate).startOf("day").toDate();
      effectiveEndDate = moment(newEndDate).endOf("day").toDate();
    } else if (valid(newStartDate) && valid(newEndDate)) {
      effectiveStartDate = moment(newStartDate).startOf("day").toDate();
      effectiveEndDate = moment(newEndDate).endOf("day").toDate();
      if (effectiveStartDate > effectiveEndDate) {
        [effectiveStartDate, effectiveEndDate] = [effectiveEndDate, effectiveStartDate];
      }
    }
    dispatch(setFilterField({ pageKey, field: "dateRange", value: [effectiveStartDate, effectiveEndDate] }));
    setPageNumber(1);
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const toDate = (v) => {
        if (v == null) return null;
        const d = typeof v === "string" ? new Date(v) : v;
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
      };
      let startDate = toDate(rawStartDate);
      let endDate = toDate(rawEndDate);
      if (startDate && !endDate) {
        endDate = moment(startDate).endOf("day").toDate();
        startDate = moment(startDate).startOf("day").toDate();
      } else if (endDate && !startDate) {
        startDate = moment(endDate).startOf("day").toDate();
        endDate = moment(endDate).endOf("day").toDate();
      } else if (startDate && endDate) {
        startDate = moment(startDate).startOf("day").toDate();
        endDate = moment(endDate).endOf("day").toDate();
      }
      const formattedStartDate = startDate ? moment(startDate).format("YYYY-MM-DD") : null;
      const formattedEndDate = endDate ? moment(endDate).format("YYYY-MM-DD") : null;

      let locationFilterValue = null;
      if (isMain && location.value !== "all" && location.value != null) {
        const locValue = typeof location.value === "number" ? location.value : parseInt(location.value, 10);
        if (!isNaN(locValue) && locValue > 0) locationFilterValue = locValue;
      }

      let supplierFilterValue = null;
      if (supplier.value !== "all" && supplier.value != null) {
        const supValue = typeof supplier.value === "number" ? supplier.value : parseInt(supplier.value, 10);
        if (!isNaN(supValue) && supValue > 0) supplierFilterValue = supValue;
      }

      const itemIdsParam =
        itemFilter.length > 0
          ? itemFilter
              .map((p) => p?.menuItem_ID ?? p?.MenuItem_ID)
              .filter((id) => id != null && id !== "")
              .join(",")
          : null;

      const params = {
        CompID,
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        LocationFilter: locationFilterValue,
        SupplierFilter: supplierFilterValue,
        SearchTerm: search?.trim() || null,
        ItemIds: itemIdsParam || undefined,
        SortBy: sortBy.value,
        SortOrder: sortOrder.value,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };

      const response = await axiosInstance.get(APIs.GET_PURCHASE_SUPPLIER_REPORT, { params });
      const data = response.data?.data ?? response.data ?? [];
      const totalCount = response.data?.totalRecords ?? response.data?.TotalRecords ?? 0;
      const pages = response.data?.totalPages ?? response.data?.TotalPages ?? 0;

      setReportData(Array.isArray(data) ? data : []);
      setTotalRecords(totalCount);
      setTotalPages(pages);
    } catch (error) {
      console.error("Error fetching purchase report:", error);
      showErrorToast(error.response?.data?.message || error.message || "Failed to fetch purchase report.");
      setReportData([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [CompID, isMain, search, sortBy, sortOrder, pageNumber, pageSize, rawStartDate, rawEndDate, location, supplier, itemFilter]);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const base = [{ value: parentId, label: companyName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          return idNum != null && !isNaN(idNum) && pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => ({
          value: (() => {
            const id = loc.CompID ?? loc.compID;
            return typeof id === "number" ? id : parseInt(id, 10);
          })(),
          label: String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${loc.CompID ?? loc.compID ?? ""}`,
        }))
        .filter((o) => o.value != null && !isNaN(o.value));
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast(error.response?.data?.message || "Error loading locations");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, companyName]);

  const fetchSuppliers = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingSuppliers(true);
      const response = await axiosInstance.get(APIs.GET_ALL_SUPPLIERS, { params: { CompID } });
      const supplierData = response.data?.data ?? response.data ?? [];
      const opts = [
        { value: "all", label: "All Suppliers" },
        ...(Array.isArray(supplierData) ? supplierData.map((s) => ({
          value: s.S_ID ?? s.s_ID ?? s.id,
          label: s.S_Name ?? s.s_Name ?? s.name ?? `Supplier ${s.S_ID ?? s.s_ID ?? ""}`,
        })) : []),
      ];
      setSuppliers(opts);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      showErrorToast(error.response?.data?.message || "Error loading suppliers");
    } finally {
      setLoadingSuppliers(false);
    }
  }, [CompID]);

  const fetchFilterProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoadingFilterProducts(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PRODUCTS_FOR_PURCHASE_ORDER, { params: { CompID } });
      const data = response.data?.data ?? response.data?.Data ?? response.data ?? [];
      setFilterProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching products for filter:", error);
      showErrorToast(error.response?.data?.message || "Error loading products");
      setFilterProducts([]);
    } finally {
      setLoadingFilterProducts(false);
    }
  }, [CompID]);

  const [isInitialized, setIsInitialized] = useState(false);
  const isFirstRender = useRef(true);
  const hasInitialFetch = useRef(false);
  const preferencesInitialized = useRef(false);

  useEffect(() => {
    if (preferencesInitialized.current) return;
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(setVisibleColumns({ pageKey, columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns) }));
    }
    if (!preferences.sortOrder) dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    if (!preferences.sortBy) dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    if (!preferences.location) dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (!preferences.supplier) dispatch(setFilterField({ pageKey, field: "supplier", value: defaultSupplier }));
    if (!preferences.dateRange) dispatch(setFilterField({ pageKey, field: "dateRange", value: defaultDateRange }));
    if (preferences.itemFilter === undefined) dispatch(setFilterField({ pageKey, field: "itemFilter", value: defaultItemFilter }));
    const timer = setTimeout(() => { preferencesInitialized.current = true; }, 50);
    return () => clearTimeout(timer);
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (isInitialized || hasInitialFetch.current) return;
    if (preferences.visibleColumns && preferences.sortOrder && preferences.sortBy &&
        preferences.location && preferences.supplier && preferences.dateRange &&
        preferences.itemFilter !== undefined) {
      setIsInitialized(true);
      hasInitialFetch.current = true;
      const timer = setTimeout(() => fetchReportData(), 150);
      return () => clearTimeout(timer);
    }
  }, [preferences.visibleColumns, preferences.sortOrder, preferences.sortBy,
      preferences.location, preferences.supplier, preferences.dateRange, preferences.itemFilter]);

  useEffect(() => {
    if (!isInitialized || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!hasInitialFetch.current) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const timeoutId = setTimeout(() => fetchReportData(), 300);
    return () => clearTimeout(timeoutId);
  }, [location, supplier, rawStartDate, rawEndDate, itemFilter, isInitialized]);

  useEffect(() => {
    if (showItemFilterModal) fetchFilterProducts();
  }, [showItemFilterModal, fetchFilterProducts]);

  useEffect(() => {
    if (isMain) fetchLocations();
    fetchSuppliers();
  }, [isMain, fetchLocations, fetchSuppliers]);

  // When user/account changes (login, logout+login as different account), reset location and refetch so data loads without refresh
  useEffect(() => {
    if (!CompID) return;
    dispatch(setFilterField({ pageKey, field: "location", value: defaultLocation }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, dispatch, pageKey]);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const paginationFirstRun = useRef(true);
  useEffect(() => {
    if (!isInitialized || !hasInitialFetch.current) return;
    if (paginationFirstRun.current) {
      paginationFirstRun.current = false;
      return;
    }
    fetchReportData();
  }, [pageNumber, pageSize, sortBy, sortOrder, isInitialized]);

  const getReceivedOrderedQty = (row) => {
    const received = parseFloat(row.quantityReceived ?? row.QuantityReceived ?? 0) ?? 0;
    const ordered = parseFloat(row.quantityOrdered ?? row.QuantityOrdered ?? 0) ?? 0;
    return `${received} / ${ordered}`;
  };

  const purchaseReportColumnDefinitions = {
    itemName: "Menu Item Name",
    purchaseDate: "Purchase Date",
    supplierName: "Supplier Name",
    receivedOrderedQty: "Received / Ordered Qty",
    unitPrice: "Unit Price",
    totalCost: "Total Cost",
    paymentStatus: "Payment Status",
    invoiceNumber: "PO Name",
    poId: "PO ID",
    goToPO: " ",
  };

  const formatDateWithFullMonth = (dateVal) => {
    if (!dateVal) return "-";
    const d = typeof dateVal === "string" ? new Date(dateVal) : dateVal;
    return d instanceof Date && !isNaN(d.getTime()) ? moment(d).format("D MMMM YYYY") : "-";
  };

  const getPurchaseOrderItemsUrl = (row) => {
    const id = row?.pO_ID ?? row?.PO_ID ?? row?.poId;
    if (id == null || id === "") return null;
    const num = typeof id === "number" ? id : parseInt(id, 10);
    return !isNaN(num) && num > 0 ? `/purchaseOrder/updatePurchaseOrderItems/${num}` : null;
  };

  const handleItemFilterSubmit = useCallback((newList) => {
    dispatch(setFilterField({ pageKey, field: "itemFilter", value: newList ?? [] }));
    setShowItemFilterModal(false);
    setPageNumber(1);
  }, [dispatch, pageKey]);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item"><a href="#/report">Report</a></li>
            <li className="breadcrumb-item active">Item Purchase Report</li>
          </ol>
          <h1 className="page-header mb-0">Item Purchase Report</h1>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-xl-2 col-lg-3 col-md-6">
              <div className="filter-control" style={{ position: "relative", zIndex: 9999, width: "100%" }}>
                <DateRangePicker
                  startDate={rawStartDate ? (typeof rawStartDate === "string" ? new Date(rawStartDate) : rawStartDate) : null}
                  endDate={rawEndDate ? (typeof rawEndDate === "string" ? new Date(rawEndDate) : rawEndDate) : null}
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                  className="filter-dropdown"
                  allowSingleDate={true}
                  allowDateRange={true}
                  popperProps={{ strategy: "fixed", modifiers: [{ name: "preventOverflow", options: { boundary: "viewport" } }] }}
                />
              </div>
            </div>
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-2 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(opt) => handleFilterChange("location", opt)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>
            )}
            <div className="col-xl-2 col-lg-2 col-md-6">
              <div className="filter-control">
                <Select
                  value={supplier}
                  onChange={(opt) => handleFilterChange("supplier", opt)}
                  options={supplierOptions}
                  styles={dropdownStyles}
                  isSearchable={true}
                  isLoading={loadingSuppliers}
                  placeholder="Supplier"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
            <div className="col-xl-4 col-lg-4 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search supplier, item, invoice..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  style={{ backgroundColor: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.3)", color: "#333" }}
                />
              </div>
            </div>
            <div className="col-xl-2 col-lg-3 col-md-12">
              <div className="d-flex gap-2 justify-content-end">
                <button className="clear-btn" onClick={clearAllFilters} title="Clear all filters">
                  <i className="fa fa-times me-1"></i> Filter
                </button>
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) => dispatch(setVisibleColumns({ pageKey, columns: cols }))}
                  columnDefinitions={purchaseReportColumnDefinitions}
                />
              </div>
            </div>
          </div>
          <div className="row g-3 align-items-center">
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="filter-control">
                <Select
                  value={sortBy}
                  onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                  options={sortByOptions}
                  styles={dropdownStyles}
                  placeholder="Sort By"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6">
              <div className="filter-control">
                <SortOrderDropDownSelection
                  defaultValue={sortOrder}
                  onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-12 d-flex justify-content-end align-items-center">
              <div className="filter-control d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setShowItemFilterModal(true)}
                  style={{
                    backgroundColor: "rgba(255,255,255,0.95)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    color: "#333",
                    fontWeight: 500,
                  }}
                  title="Filter by items"
                >
                  <i className="fa fa-list me-1"></i>
                  {itemFilter.length > 0 ? `${itemFilter.length} item(s)` : "Select items"}
                </button>
                {itemFilter.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm p-1"
                    onClick={clearItemFilter}
                    title="Clear item filter"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.8)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      color: "#333",
                    }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div
                className="table-responsive mt-3"
                style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto" }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      {visibleColumns.itemName && <th className="pt-2 pb-2">Menu Item Name</th>}
                      {visibleColumns.purchaseDate && <th className="pt-2 pb-2">Purchase Date</th>}
                      {visibleColumns.supplierName && <th className="pt-2 pb-2">Supplier Name</th>}
                      {visibleColumns.receivedOrderedQty && <th className="pt-2 pb-2 text-center">Received / Ordered Qty</th>}
                      {visibleColumns.unitPrice && <th className="pt-2 pb-2 text-center">Unit Price</th>}
                      {visibleColumns.totalCost && <th className="pt-2 pb-2 text-center">Total Cost</th>}
                      {visibleColumns.paymentStatus && <th className="pt-2 pb-2 text-center">Payment Status</th>}
                      {visibleColumns.invoiceNumber && <th className="pt-2 pb-2 text-center">PO Name</th>}
                      {visibleColumns.poId && <th className="pt-2 pb-2 text-center">PO ID</th>}
                      {visibleColumns.goToPO && <th className="pt-2 pb-2 text-center" style={{ width: "48px" }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter((k) => visibleColumns[k]).length} className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading...</span>
                        </td>
                      </tr>
                    ) : reportData.length === 0 ? (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter((k) => visibleColumns[k]).length} className="text-center py-4">
                          <div className="text-muted">
                            <i className="fa fa-search fa-2x mb-2"></i>
                            <p className="mb-0">
                              {search.trim() ? "No purchase records found matching your search." : "No purchase data available."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      reportData.map((row, index) => {
                        const poItemsUrl = getPurchaseOrderItemsUrl(row);
                        const poIdDisplay = row.pO_ID ?? row.PO_ID ?? row.poId ?? "-";
                        return (
                          <tr key={row.reportRowId ?? row.ReportRowId ?? index}>
                            {visibleColumns.itemName && (
                              <td className="align-middle">{row.itemName ?? row.ItemName ?? "-"}</td>
                            )}
                            {visibleColumns.purchaseDate && (
                              <td className="align-middle">{formatDateWithFullMonth(row.purchaseDate ?? row.PurchaseDate)}</td>
                            )}
                            {visibleColumns.supplierName && (
                              <td className="align-middle">{row.supplierName ?? row.SupplierName ?? "-"}</td>
                            )}
                            {visibleColumns.receivedOrderedQty && (
                              <td className="align-middle text-center">{getReceivedOrderedQty(row)}</td>
                            )}
                            {visibleColumns.unitPrice && (
                              <td className="align-middle text-center">
                                {row.unitPrice != null || row.UnitPrice != null
                                  ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.unitPrice ?? row.UnitPrice ?? 0).toFixed(2)}`
                                  : "-"}
                              </td>
                            )}
                            {visibleColumns.totalCost && (
                              <td className="align-middle text-center">
                                {row.totalCost != null || row.TotalCost != null
                                  ? `${getCurrencySymbol(compCurrency)}${parseFloat(row.totalCost ?? row.TotalCost ?? 0).toFixed(2)}`
                                  : "-"}
                              </td>
                            )}
                            {visibleColumns.paymentStatus && (
                              <td className="align-middle text-center">{row.paymentStatus ?? row.PaymentStatus ?? "-"}</td>
                            )}
                            {visibleColumns.invoiceNumber && (
                              <td className="align-middle text-center">{row.invoiceNumber ?? row.InvoiceNumber ?? "-"}</td>
                            )}
                            {visibleColumns.poId && (
                              <td className="align-middle text-center">{poIdDisplay}</td>
                            )}
                            {visibleColumns.goToPO && (
                              <td className="align-middle text-center" style={{ width: "48px" }}>
                                {poItemsUrl ? (
                                  <a
                                    href={poItemsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open Purchase Order Items in new tab"
                                    style={{ color: themeColor, fontSize: "1rem" }}
                                  >
                                    <i className="fa fa-external-link-alt"></i>
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </div>

      <ProductMultiSelectionModal
        showModal={showItemFilterModal}
        setShowModal={setShowItemFilterModal}
        showButton={true}
        selectedProducts={itemFilter}
        setSelectedProducts={handleItemFilterSubmit}
        products={filterProducts}
        selectionPrompt="Select items to filter the purchase report. Only purchase order lines for these items will be shown."
      />
    </div>
  );
};

export default PurchaseSupplierReport;
